// Le Parcours (D-161, D-162), premier jeu d'Arceau Game, sur le moteur commun (src/domain/game.ts). La boucle de l'oie :
// les équipes (les chasubles de l'exercice) ou chacun pour soi avancent sur un chemin de N cases dessiné sur un
// demi-terrain, en réussissant des défis. Logique pure, hasard et horloge injectés ; chaque geste rend le nouvel état et
// les effets à montrer (le dé, le pion case par case, la carte qui se retourne, l'arrivée).
//
// Les règles (D-162) : 20 cases par défaut (12 court, 30 long) ; un dé de 1 à 6 ; la première équipe qui atteint la
// dernière case gagne (pas de compte exact), les autres sont classées par case ; une case d'action sur quatre, toujours
// une bonne nouvelle au lot 1 (Tir bonus : 3 tirs, +1 par panier ; Contre-attaque : +3 ; Lancers francs : 2 lancers,
// +1 par panier), le coach peut les couper ; pas de chrono imposé (option « Arrêter à 15 min »). Choix de ce lot :
//  * seul le dé déclenche une case (un pion poussé par un effet ne tire ni carte ni action) ;
//  * « Passe un tour » : l'équipe attend que chaque autre équipe encore en course ait lancé le dé une fois ;
//  * l'action « le pion de tête recule de 1 » vise le ou les premiers parmi les AUTRES (jamais celle qui la tire) ;
//  * une carte Joker se garde ; « Change de défi » se joue pendant un défi, « Annule un raté » au moment du « Raté ».

import {
  act, createGame, gameMs, invalidateLast, lastDeclared, lastUndoable, setPaused, shuffled, undoLast,
  type GameMode, type GameState, type Participant,
} from './game';
import {
  ACTION_LABEL, FAM_LABEL, JOKER_LABEL, STARTER_LABEL, cardFromExercise, cardTags, cardTitle, readDeck, starterDeck, starterFor, toParcoursCard,
  type ActionFx, type Card, type Fam, type JokerFx,
} from './game-cards';
import { BIBS, BIB_HEX, BIB_INK, isBib, type Bib, type ExoGroup } from './groups';
import type { TeamGender } from './labels';
import type { Category } from './session';

// -----------------------------------------------------------------------------------------------------------------
// Les réglages (games.config, relus sans confiance)
// -----------------------------------------------------------------------------------------------------------------
export type BoardLength = 12 | 20 | 30;
export const LENGTHS: BoardLength[] = [12, 20, 30];
export const LENGTH_LABEL: Record<BoardLength, string> = { 12: 'Court', 20: 'Moyen', 30: 'Long' };
/** La durée annoncée d'une partie, selon le plateau. */
export const ABOUT_MIN: Record<BoardLength, number> = { 12: 7, 20: 12, 30: 18 };
export type FailRule = 'retry' | 'back2' | 'change';
export const FAIL_RULES: FailRule[] = ['retry', 'back2', 'change'];
export const FAIL_LABEL: Record<FailRule, { title: string; sub: string; short: string }> = {
  retry: { title: 'Réessaie', sub: 'la même carte, jusqu’à la réussir', short: 'réessaie' },
  back2: { title: 'Recule de 2', sub: 'puis l’équipe relance le dé', short: 'recule de 2' },
  change: { title: 'Change de carte', sub: 'on en retourne une autre', short: 'nouvelle carte' },
};
export const TEAMS_MAX = 6;
export const MEMBERS_MAX = 30;

export interface ParcoursTeam { bib: Bib; name: string; members: string[] }
export interface ParcoursConfig {
  mode: GameMode;
  /** En équipes : 2 à 6, les chasubles de l'exercice. */
  teams: ParcoursTeam[];
  /** Chacun pour soi : 2 à 6 personnes (prénom seul à l'écran). */
  solo: string[];
  length: BoardLength;
  fail: FailRule;
  /** Les cases d'action (le coach peut les couper). */
  actions: boolean;
  /** « Arrêter à 15 min » (sans : la partie finit à l'arrivée). */
  stop15: boolean;
  sound: boolean;
  /** Les téléphones par QR (D-167) : un par équipe ; le téléphone du coach en télécommande. En équipes seulement. */
  phones: boolean;
  remote: boolean;
  deckName: string;
  deck: Card[];
}

const NAMES_F: Record<Bib, string> = { red: 'Les Rouges', blue: 'Les Bleues', yellow: 'Les Jaunes', green: 'Les Vertes', orange: 'Les Orange', purple: 'Les Violettes' };
const NAMES_M: Record<Bib, string> = { red: 'Les Rouges', blue: 'Les Bleus', yellow: 'Les Jaunes', green: 'Les Verts', orange: 'Les Orange', purple: 'Les Violets' };
/** Le nom d'une équipe d'après sa chasuble, accordé au genre de l'équipe. */
export const teamName = (bib: Bib, gender: TeamGender) => (gender === 'F' ? NAMES_F : NAMES_M)[bib];
/** La lettre du maillot-pion. */
export const BIB_LETTER: Record<Bib, string> = { red: 'R', blue: 'B', yellow: 'J', green: 'V', orange: 'O', purple: 'Vi' };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ids = (v: unknown, max: number) => (Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && UUID.test(x)))].slice(0, max) : []);
const str = (v: unknown, max: number) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** Les réglages d'une partie, relus sans confiance (tout ce qui manque prend sa valeur par défaut). */
export function readParcoursConfig(v: unknown, gender: TeamGender = 'X'): ParcoursConfig {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const used = new Set<Bib>();
  const teams: ParcoursTeam[] = [];
  for (const t of Array.isArray(o.teams) ? o.teams.slice(0, TEAMS_MAX) : []) {
    if (!t || typeof t !== 'object') continue;
    const x = t as Record<string, unknown>;
    const bib = isBib(x.bib) && !used.has(x.bib) ? x.bib : BIBS.find(b => !used.has(b))!;
    used.add(bib);
    teams.push({ bib, name: str(x.name, 24) || teamName(bib, gender), members: ids(x.members, MEMBERS_MAX) });
  }
  const length = LENGTHS.includes(o.length as BoardLength) ? o.length as BoardLength : 20;
  const deck = readDeck(o.deck);
  return {
    mode: o.mode === 'solo' ? 'solo' : 'teams',
    teams,
    solo: ids(o.solo, TEAMS_MAX),
    length,
    fail: FAIL_RULES.includes(o.fail as FailRule) ? o.fail as FailRule : 'back2',
    actions: o.actions !== false,
    stop15: o.stop15 === true,
    sound: o.sound !== false,
    phones: o.phones !== false,
    remote: o.remote !== false,
    deckName: str(o.deckName, 40) || `Paquet ${STARTER_LABEL.u13}`,
    deck: deck.length ? deck : starterDeck('u13'),
  };
}

/** Ce qui manque pour lancer : deux équipes (ou deux personnes) au moins, une carte au moins. */
export function launchProblem(c: ParcoursConfig): string | null {
  if (c.mode === 'teams' && c.teams.length < 2) return 'Il faut deux équipes au moins.';
  if (c.mode === 'teams' && c.teams.some(t => !t.members.length)) return 'Une équipe est vide : ajoute quelqu’un ou retire-la.';
  if (c.mode === 'solo' && c.solo.length < 2) return 'Choisis deux personnes au moins.';
  if (!c.deck.some(x => x.kind === 'defi')) return 'Le paquet n’a aucun défi.';
  return null;
}

/** Les réglages d'une partie faite depuis un exercice : ses groupes deviennent les équipes, l'exercice une carte. */
export function configFromExercise(o: {
  groups: ExoGroup[]; present?: string[] | null; category: string | null; gender: TeamGender;
  exercise?: { id?: string | null; title: string; description?: string | null; category?: Category | null } | null;
}): ParcoursConfig {
  const here = o.present ? new Set(o.present) : null;
  const used = new Set<Bib>();
  const teams: ParcoursTeam[] = [];
  for (const g of o.groups) {
    if (teams.length >= TEAMS_MAX) break;
    const members = g.players.filter(p => !here || here.has(p));
    if (!members.length) continue;
    const bib = g.bib && !used.has(g.bib) ? g.bib : BIBS.find(b => !used.has(b))!;
    used.add(bib);
    teams.push({ bib, name: teamName(bib, o.gender), members: members.slice(0, MEMBERS_MAX) });
  }
  if (teams.length < 2) {
    // Pas de groupes : deux équipes, les présents répartis un sur deux (le coach change à la préparation).
    const pool = o.present ?? o.groups.flatMap(g => g.players);
    teams.splice(0, teams.length,
      { bib: 'red', name: teamName('red', o.gender), members: pool.filter((_, i) => i % 2 === 0).slice(0, MEMBERS_MAX) },
      { bib: 'blue', name: teamName('blue', o.gender), members: pool.filter((_, i) => i % 2 === 1).slice(0, MEMBERS_MAX) });
  }
  const k = starterFor(o.category);
  const deck = starterDeck(k);
  let deckName = `Paquet ${STARTER_LABEL[k]}`;
  if (o.exercise) {
    // « En faire une carte » : l'exercice prend la place du premier défi de sa famille.
    const mine = cardFromExercise(o.exercise);
    const at = deck.findIndex(c => c.kind === 'defi' && c.fam === mine.fam);
    deck.splice(at >= 0 ? at : 0, 1, mine);
    const word = o.exercise.title.trim().split(/\s+/)[0] ?? '';
    const cat = /^U\s?\d{1,2}/i.exec(o.category ?? '')?.[0]?.replace(/\s/g, '').toUpperCase() ?? (o.category ? o.category.toLowerCase() : STARTER_LABEL[k]);
    if (word) deckName = `${word.charAt(0).toUpperCase()}${word.slice(1)} ${cat}`.slice(0, 40);
  }
  return { mode: 'teams', teams, solo: [], length: 20, fail: 'back2', actions: true, stop15: false, sound: true, phones: true, remote: true, deckName, deck };
}

/** Les participants d'une partie : les équipes (couleur de la chasuble), ou les personnes (une couleur chacune, l'initiale). */
export function participantsOf(c: ParcoursConfig, nameOf: (personId: string) => string): Participant[] {
  if (c.mode === 'solo') {
    return c.solo.slice(0, TEAMS_MAX).map((id, i) => {
      const bib = BIBS[i]!;
      const name = nameOf(id) || `Pion ${i + 1}`;
      return { id: `p${i}`, name, color: BIB_HEX[bib], ink: BIB_INK[bib], letter: name.charAt(0).toUpperCase(), members: [id] };
    });
  }
  return c.teams.map((t, i) => ({ id: `t${i}`, name: t.name, color: BIB_HEX[t.bib], ink: BIB_INK[t.bib], letter: BIB_LETTER[t.bib], members: t.members }));
}

// -----------------------------------------------------------------------------------------------------------------
// Le plateau
// -----------------------------------------------------------------------------------------------------------------
export type ActionCase = 'tir' | 'contre' | 'lf';
export const ACTION_CASE_LABEL: Record<ActionCase, string> = { tir: 'Tir bonus', contre: 'Contre-attaque', lf: 'Lancers francs' };
export const ACTION_CASE_RULE: Record<ActionCase, string> = {
  tir: '3 tirs : chaque panier, une case de plus, tout de suite. Pas de carte.',
  contre: 'Une contre-attaque éclair : +3 cases, tout de suite.',
  lf: '2 lancers francs : chaque panier, une case de plus, tout de suite.',
};
const ACTION_CYCLE: ActionCase[] = ['tir', 'contre', 'tir', 'lf'];
/** La case d'action d'une case (une sur quatre, ni le départ ni l'arrivée), ou null. */
export function actionAt(n: number, i: number, on = true): ActionCase | null {
  if (!on || i <= 0 || i >= n || i % 4 !== 0) return null;
  return ACTION_CYCLE[(i / 4 - 1) % ACTION_CYCLE.length]!;
}
export const shotsOf = (a: ActionCase) => (a === 'tir' ? 3 : a === 'lf' ? 2 : 0);

// -----------------------------------------------------------------------------------------------------------------
// L'état de la partie
// -----------------------------------------------------------------------------------------------------------------
export type Phase = 'roll' | 'defi' | 'bonus' | 'done';
export interface PState {
  at: number;
  phase: Phase;
  /** Le défi en cours (et son rang dans le paquet). */
  card: Card | null;
  cardIdx: number | null;
  /** Début du défi, en temps de jeu. */
  since: number;
  bonus: ActionCase | null;
  shots: boolean[];
  jokers: JokerFx[];
  rolls: number;
  /** « Passe un tour » : les lancers des autres au moment de la sanction. */
  skip: Record<string, number> | null;
  ok: number;
  ko: number;
  /** Temps passé sur les défis réussis. */
  okMs: number;
  /** La dernière règle appliquée, en une ligne (« Raté · recule de 2 »). */
  note: string | null;
  noteKo: boolean;
}
export interface Rules { n: BoardLength; fail: FailRule; actions: boolean; deck: Card[] }
export interface Shared {
  rules: Rules;
  /** La pioche (rangs du paquet, la prochaine à la fin) et les cartes jouées. */
  draw: number[];
  used: number[];
  /** Par carte : essais déclarés et ratés. */
  cardStats: Record<number, { tries: number; ko: number }>;
  winner: string | null;
  endedAt: number | null;
  endedBy: 'arrival' | 'time' | 'coach' | null;
}
export type ParcoursState = GameState<PState, Shared>;

/** Ce que l'écran montre, dans l'ordre : le dé, le pion case par case, la carte, une bulle, l'arrivée. */
export type Fx =
  | { t: 'die'; who: string; n: number }
  | { t: 'move'; who: string; from: number; to: number }
  | { t: 'card'; who: string; card: Card }
  | { t: 'bonus'; who: string; kind: ActionCase }
  | { t: 'note'; who: string; text: string; ko?: boolean; at: number }
  | { t: 'arrive'; who: string };
export interface Step { g: ParcoursState; fx: Fx[] }

const fresh = (): PState => ({ at: 0, phase: 'roll', card: null, cardIdx: null, since: 0, bonus: null, shots: [], jokers: [], rolls: 0, skip: null, ok: 0, ko: 0, okMs: 0, note: null, noteKo: false });

export function newParcours(c: ParcoursConfig, participants: Participant[], rand: () => number, now: number): ParcoursState {
  // Un paquet gardé depuis Le Sablier (D-167) : ses actions se lisent comme celles du Parcours.
  const deck = (c.deck.length ? c.deck : starterDeck('u13')).map(toParcoursCard);
  const shared: Shared = {
    rules: { n: c.length, fail: c.fail, actions: c.actions, deck },
    draw: shuffled(deck.map((_, i) => i), rand), used: [], cardStats: {}, winner: null, endedAt: null, endedBy: null,
  };
  return createGame<PState, Shared>(c.mode, participants, fresh, shared, now);
}

export const nameOfP = (g: ParcoursState, id: string) => g.participants.find(p => p.id === id)?.name ?? '';
const others = (g: ParcoursState, who: string) => g.participants.filter(p => p.id !== who && g.ps[p.id]!.phase !== 'done').map(p => p.id);

/** « Passe un tour » : vrai tant qu'une autre équipe encore en course n'a pas relancé depuis la sanction. */
export function skipping(g: ParcoursState, who: string): boolean {
  const s = g.ps[who]?.skip;
  if (!s) return false;
  return others(g, who).some(id => (g.ps[id]!.rolls) <= (s[id] ?? -1));
}
export const canRoll = (g: ParcoursState, who: string) => !g.over && g.clock.pausedAt === null && g.ps[who]?.phase === 'roll' && !skipping(g, who);

/** Tire une carte : la pioche, remélangée avec les cartes jouées quand elle est vide (sauf les défis en cours). */
function drawCard(s: Shared, g: ParcoursState, rand: () => number): { idx: number; shared: Shared } {
  let draw = s.draw, used = s.used;
  if (!draw.length) {
    const busy = new Set(Object.values(g.ps).map(p => p.cardIdx).filter((x): x is number => x !== null));
    const back = used.filter(i => !busy.has(i));
    draw = shuffled(back.length ? back : s.rules.deck.map((_, i) => i), rand);
    used = used.filter(i => busy.has(i));
  }
  const idx = draw[draw.length - 1]!;
  return { idx, shared: { ...s, draw: draw.slice(0, -1), used: [...used, idx] } };
}

/** L'arrivée : la partie est finie, ce participant gagne. */
const arrive = (s: Shared, who: string, at: number): Shared => ({ ...s, winner: who, endedAt: at, endedBy: 'arrival' });

/** Lancer le dé : le pion avance, puis la case dit la suite (une carte, une action, l'arrivée). */
export function roll(g: ParcoursState, who: string, rand: () => number, now: number): Step {
  if (!canRoll(g, who)) return { g, fx: [] };
  const n = g.shared.rules.n;
  const p = g.ps[who]!;
  const die = 1 + Math.floor(rand() * 6);
  const t = gameMs(g, now);
  const fx: Fx[] = [{ t: 'die', who, n: die }];
  let np: PState = { ...p, rolls: p.rolls + 1, skip: null, note: null, noteKo: false };
  let shared = g.shared;
  const ps: Record<string, PState> = {};
  const from = p.at;
  let to = Math.min(n, from + die);
  fx.push({ t: 'move', who, from, to });
  let what = '';
  const finish = (at: number) => { np = { ...np, at: n, phase: 'done' }; shared = arrive(shared, who, at); fx.push({ t: 'arrive', who }); what = ', arrivée'; };
  if (to >= n) finish(t);
  else {
    np.at = to;
    const a = actionAt(n, to, shared.rules.actions);
    if (a === 'contre') {
      const to2 = Math.min(n, to + 3);
      fx.push({ t: 'bonus', who, kind: a }, { t: 'move', who, from: to, to: to2 }, { t: 'note', who, text: '+3', at: to2 });
      what = `, Contre-attaque : case ${to2}`;
      np.at = to2;
      if (to2 >= n) finish(t);
    } else if (a) {
      np = { ...np, phase: 'bonus', bonus: a, shots: [] };
      fx.push({ t: 'bonus', who, kind: a });
      what = `, ${ACTION_CASE_LABEL[a]}`;
    } else {
      const dr = drawCard(shared, g, rand);
      shared = dr.shared;
      const card = shared.rules.deck[dr.idx]!;
      fx.push({ t: 'card', who, card });
      if (card.kind === 'defi') {
        np = { ...np, phase: 'defi', card, cardIdx: dr.idx, since: t };
        what = `, Défi ${FAM_LABEL[card.fam ?? 'tir']}`;
      } else if (card.kind === 'joker') {
        np = { ...np, jokers: [...np.jokers, card.fx as JokerFx] };
        what = `, Joker « ${JOKER_LABEL[card.fx as JokerFx].toLowerCase()} »`;
      } else {
        const fxa = card.fx as ActionFx;
        what = `, Action « ${ACTION_LABEL[fxa].toLowerCase()} »`;
        if (fxa === 'plus2') {
          const to3 = Math.min(n, np.at + 2);
          fx.push({ t: 'move', who, from: np.at, to: to3 }, { t: 'note', who, text: '+2', at: to3 });
          np.at = to3;
          if (to3 >= n) finish(t);
        } else if (fxa === 'leaderBack') {
          const rivals = others(g, who);
          const top = Math.max(0, ...rivals.map(id => g.ps[id]!.at));
          if (top > 0) for (const id of rivals.filter(id => g.ps[id]!.at === top)) {
            ps[id] = { ...g.ps[id]!, at: top - 1 };
            fx.push({ t: 'move', who: id, from: top, to: top - 1 }, { t: 'note', who: id, text: '−1', ko: true, at: top - 1 });
          }
        }
      }
    }
  }
  ps[who] = np;
  const next = act(g, now, { who, kind: 'roll', text: `dé ${die} : case ${from} → ${to}${what}` }, { ps, shared, over: shared.endedBy !== null || undefined });
  return { g: next, fx };
}

/** « Fait ! » ou « Raté » : l'équipe déclare elle-même. Réussi : l'effet de la carte ; raté : la règle de la partie. */
export function declare(g: ParcoursState, who: string, ok: boolean, rand: () => number, now: number, opts: { joker?: boolean } = {}): Step {
  const p = g.ps[who];
  if (!p || p.phase !== 'defi' || !p.card || g.over || g.clock.pausedAt !== null) return { g, fx: [] };
  const n = g.shared.rules.n;
  const t = gameMs(g, now);
  const card = p.card;
  const title = cardTitle(card);
  const fx: Fx[] = [];
  const st = p.cardIdx !== null ? g.shared.cardStats[p.cardIdx] ?? { tries: 0, ko: 0 } : null;
  let shared: Shared = st && p.cardIdx !== null
    ? { ...g.shared, cardStats: { ...g.shared.cardStats, [p.cardIdx]: { tries: st.tries + 1, ko: st.ko + (ok ? 0 : 1) } } } : g.shared;
  if (ok) {
    let np: PState = { ...p, ok: p.ok + 1, okMs: p.okMs + Math.max(0, t - p.since), phase: 'roll', card: null, cardIdx: null, note: null, noteKo: false };
    const plus = card.win === 'plus2' ? 2 : card.win === 'plus1' ? 1 : 0;
    let tail = ' : relance';
    if (plus) {
      const to = Math.min(n, p.at + plus);
      fx.push({ t: 'move', who, from: p.at, to }, { t: 'note', who, text: `+${plus}`, at: to });
      np.at = to;
      tail = ` : +${plus} case${plus > 1 ? 's' : ''} (${to})`;
      if (to >= n) { np = { ...np, phase: 'done' }; shared = arrive(shared, who, t); fx.push({ t: 'arrive', who }); tail += ', arrivée'; }
    }
    const next = act(g, now, { who, kind: 'done', text: `« Fait ! » ${title}${tail}`, declared: true }, { ps: { [who]: np }, shared, over: shared.endedBy !== null || undefined });
    return { g: next, fx };
  }
  let np: PState = { ...p, ko: p.ko + 1, noteKo: true };
  let tail: string;
  if (opts.joker && p.jokers.includes('cancel')) {
    const k = p.jokers.indexOf('cancel');
    np = { ...np, jokers: p.jokers.filter((_, i) => i !== k), phase: 'roll', card: null, cardIdx: null, note: 'Raté · joker : rien ne change', noteKo: false };
    tail = ' : joker « annule un raté »';
  } else if (g.shared.rules.fail === 'back2') {
    const to = Math.max(0, p.at - 2);
    fx.push({ t: 'move', who, from: p.at, to }, { t: 'note', who, text: 'Recule de 2', ko: true, at: to });
    np = { ...np, at: to, phase: 'roll', card: null, cardIdx: null, note: 'Raté · recule de 2' };
    tail = ` : recule de 2 (${p.at} → ${to})`;
  } else if (g.shared.rules.fail === 'change') {
    const dr = drawCard(shared, g, rand);
    shared = dr.shared;
    const nc = shared.rules.deck[dr.idx]!;
    tail = ' : nouvelle carte';
    if (nc.kind === 'defi') {
      fx.push({ t: 'card', who, card: nc });
      np = { ...np, card: nc, cardIdx: dr.idx, since: t, note: 'Raté · nouvelle carte' };
    } else {
      // Une action ou un joker tiré à la place : on le garde ou il s'applique comme au dé (sans déplacer), puis on relance.
      fx.push({ t: 'card', who, card: nc });
      np = { ...np, phase: 'roll', card: null, cardIdx: null, note: 'Raté · nouvelle carte', jokers: nc.kind === 'joker' ? [...np.jokers, nc.fx as JokerFx] : np.jokers };
    }
  } else {
    np = { ...np, note: 'Raté · réessaie' };
    tail = ' : réessaie';
  }
  const next = act(g, now, { who, kind: 'fail', text: `« Raté » ${title}${tail}` }, { ps: { [who]: np }, shared });
  return { g: next, fx };
}

/** Le joker « Change de défi » : une autre carte, sans pénalité. */
export function jokerChange(g: ParcoursState, who: string, rand: () => number, now: number): Step {
  const p = g.ps[who];
  if (!p || p.phase !== 'defi' || !p.jokers.includes('change') || g.over || g.clock.pausedAt !== null) return { g, fx: [] };
  const t = gameMs(g, now);
  // On tire jusqu'à un défi (le paquet en a toujours un).
  let shared = g.shared, nc: Card | null = null, idx = -1;
  for (let i = 0; i < shared.rules.deck.length + 1 && !nc; i++) {
    const dr = drawCard(shared, g, rand); shared = dr.shared;
    if (shared.rules.deck[dr.idx]!.kind === 'defi' && dr.idx !== p.cardIdx) { nc = shared.rules.deck[dr.idx]!; idx = dr.idx; }
  }
  if (!nc) return { g, fx: [] };
  const k = p.jokers.indexOf('change');
  const np: PState = { ...p, jokers: p.jokers.filter((_, i) => i !== k), card: nc, cardIdx: idx, since: t, note: 'Joker · change de défi', noteKo: false };
  const next = act(g, now, { who, kind: 'joker', text: `joker « change de défi » : ${cardTitle(nc)}` }, { ps: { [who]: np }, shared });
  return { g: next, fx: [{ t: 'card', who, card: nc }] };
}

/** Un tir de la case d'action (Tir bonus, Lancers francs) : marqué, le pion avance d'une case tout de suite. */
export function shot(g: ParcoursState, who: string, made: boolean, now: number): Step {
  const p = g.ps[who];
  if (!p || p.phase !== 'bonus' || !p.bonus || g.over || g.clock.pausedAt !== null) return { g, fx: [] };
  const n = g.shared.rules.n;
  const t = gameMs(g, now);
  const shots = [...p.shots, made];
  const count = shotsOf(p.bonus);
  const fx: Fx[] = [];
  let np: PState = { ...p, shots };
  let shared = g.shared;
  if (made) {
    const to = Math.min(n, p.at + 1);
    fx.push({ t: 'move', who, from: p.at, to }, { t: 'note', who, text: '+1', at: to });
    np.at = to;
    if (to >= n) { np = { ...np, phase: 'done' }; shared = arrive(shared, who, t); fx.push({ t: 'arrive', who }); }
  }
  if (np.phase !== 'done' && shots.length >= count) {
    const made2 = shots.filter(Boolean).length;
    np = { ...np, phase: 'roll', bonus: null, shots: [], note: `${ACTION_CASE_LABEL[p.bonus]} · ${made2} sur ${count}`, noteKo: false };
  }
  const k = shots.length;
  const text = `${ACTION_CASE_LABEL[p.bonus]}, ${k === 1 ? '1er' : `${k}e`} tir ${made ? `marqué : case ${np.at}` : 'raté'}`;
  const next = act(g, now, { who, kind: 'shot', text }, { ps: { [who]: np }, shared, over: shared.endedBy !== null || undefined });
  return { g: next, fx };
}

// -----------------------------------------------------------------------------------------------------------------
// L'arbitrage du coach
// -----------------------------------------------------------------------------------------------------------------
export type Sanction = 'back1' | 'back2' | 'back3' | 'skip';
export const SANCTION_LABEL: Record<Sanction, string> = { back1: 'Recule de 1', back2: 'de 2', back3: 'de 3', skip: 'Passe un tour' };

export function sanction(g: ParcoursState, who: string, s: Sanction, now: number): Step {
  const p = g.ps[who];
  if (!p || g.over || p.phase === 'done') return { g, fx: [] };
  if (s === 'skip') {
    const snap = Object.fromEntries(others(g, who).map(id => [id, g.ps[id]!.rolls]));
    const next = act(g, now, { who, kind: 'sanction', text: 'sanction : passe un tour', coach: true }, { ps: { [who]: { ...p, skip: snap, note: 'Sanction · passe un tour', noteKo: true } } });
    return { g: next, fx: [] };
  }
  const k = s === 'back1' ? 1 : s === 'back2' ? 2 : 3;
  const to = Math.max(0, p.at - k);
  const next = act(g, now, { who, kind: 'sanction', text: `sanction : recule de ${k} (${p.at} → ${to})`, coach: true },
    { ps: { [who]: { ...p, at: to, note: `Sanction · recule de ${k}`, noteKo: true } } });
  return { g: next, fx: [{ t: 'move', who, from: p.at, to }, { t: 'note', who, text: `Recule de ${k}`, ko: true, at: to }] };
}

/** Le dernier défi déclaré « Fait ! » par ce participant, encore valable (ce que « Invalider » défait). */
export function lastDone(g: ParcoursState, who: string): { title: string; backTo: number } | null {
  const e = lastDeclared(g, who, ['done']);
  const before = e?.before.ps[who];
  if (!e || !before?.card) return null;
  return { title: cardTitle(before.card), backTo: before.at };
}

/** Invalider le dernier défi : le pion revient, la carte aussi (l'équipe la refait). */
export function invalidate(g: ParcoursState, who: string, now: number): Step {
  const d = lastDone(g, who);
  const e = lastDeclared(g, who, ['done']);
  if (!d || !e) return { g, fx: [] };
  const from = g.ps[who]!.at;
  let next = invalidateLast(g, who, now, `invalide « Fait ! » ${d.title} : retour case ${d.backTo}`, ['done']);
  // L'essai ne compte plus ; la partie reprend si c'était l'arrivée.
  const idx = e.before.ps[who]!.cardIdx;
  const st = idx !== null ? next.shared.cardStats[idx] : undefined;
  let shared = next.shared;
  if (idx !== null && st) shared = { ...shared, cardStats: { ...shared.cardStats, [idx]: { ...st, tries: Math.max(0, st.tries - 1) } } };
  if (shared.winner === who) shared = { ...shared, winner: null, endedAt: null, endedBy: null };
  next = { ...next, shared, ps: { ...next.ps, [who]: { ...next.ps[who]!, note: 'Invalidé par le coach', noteKo: true } } };
  return { g: next, fx: [{ t: 'move', who, from, to: d.backTo }, { t: 'note', who, text: 'Invalidé', ko: true, at: d.backTo }] };
}

/** La dernière action, en phrase, pour « Annuler la dernière action ». */
export function lastActionText(g: ParcoursState): { text: string; at: number } | null {
  const e = lastUndoable(g);
  if (!e) return null;
  return { text: `${e.who ? `${nameOfP(g, e.who)} · ` : ''}${e.text}`, at: e.at };
}
export function undo(g: ParcoursState, now: number): Step {
  const e = lastUndoable(g);
  if (!e) return { g, fx: [] };
  const moves: Fx[] = Object.entries(e.before.ps).filter(([id, p]) => g.ps[id]!.at !== p.at).map(([id, p]) => ({ t: 'move' as const, who: id, from: g.ps[id]!.at, to: p.at }));
  const next = undoLast(g, now, `annule : ${e.who ? `${nameOfP(g, e.who)}, ` : ''}${e.text}`);
  return { g: next, fx: moves };
}
export const pause = (g: ParcoursState, on: boolean, now: number) => setPaused(g, on, now);

/** La fin sans arrivée : « Arrêter à 15 min », ou le coach arrête ; classement par case. */
export function endGame(g: ParcoursState, by: 'time' | 'coach', now: number): ParcoursState {
  if (g.over) return g;
  const t = gameMs(g, now);
  return act(g, now, { who: null, kind: 'end', text: by === 'time' ? 'fin du temps (15 min)' : 'partie arrêtée par le coach', arbitration: true },
    { shared: { ...g.shared, endedAt: t, endedBy: by }, over: true });
}
export const STOP_MS = 15 * 60_000;

// -----------------------------------------------------------------------------------------------------------------
// L'arrivée et le bilan du coach
// -----------------------------------------------------------------------------------------------------------------
export interface Rank { id: string; rank: number; at: number; arrived: boolean }
/** Le classement : le vainqueur, puis les autres par case (à égalité, même place). */
export function ranking(g: ParcoursState): Rank[] {
  const w = g.shared.winner;
  const list = g.participants.map(p => ({ id: p.id, at: g.ps[p.id]!.at, arrived: p.id === w }))
    .sort((a, b) => Number(b.arrived) - Number(a.arrived) || b.at - a.at);
  let rank = 0;
  return list.map((x, i) => {
    const prev = list[i - 1];
    if (!prev || prev.arrived !== x.arrived || prev.at !== x.at) rank = i + 1;
    return { ...x, rank };
  });
}

export interface Bilan {
  rows: { id: string; ok: number; ko: number; avgMs: number | null }[];
  missed: { card: Card; ko: number; tries: number }[];
  /** Ce qui coince : une précision (« main gauche ») ou une famille, ratée au moins une fois sur deux. */
  insight: { label: string; ko: number; tries: number; tag: boolean } | null;
}
export function bilan(g: ParcoursState): Bilan {
  const rows = g.participants.map(p => { const s = g.ps[p.id]!; return { id: p.id, ok: s.ok, ko: s.ko, avgMs: s.ok ? Math.round(s.okMs / s.ok) : null }; });
  const deck = g.shared.rules.deck;
  const stats = Object.entries(g.shared.cardStats).map(([i, s]) => ({ card: deck[Number(i)]!, ...s })).filter(x => x.card && x.tries);
  const missed = stats.filter(x => x.ko > 0).sort((a, b) => b.ko - a.ko || b.ko / b.tries - a.ko / a.tries).slice(0, 3);
  const byTag = new Map<string, { ko: number; tries: number }>();
  const byFam = new Map<Fam, { ko: number; tries: number }>();
  for (const x of stats) {
    for (const t of cardTags(x.card)) { const v = byTag.get(t) ?? { ko: 0, tries: 0 }; byTag.set(t, { ko: v.ko + x.ko, tries: v.tries + x.tries }); }
    const f = x.card.fam ?? 'tir';
    const v = byFam.get(f) ?? { ko: 0, tries: 0 }; byFam.set(f, { ko: v.ko + x.ko, tries: v.tries + x.tries });
  }
  const pick = <K>(m: Map<K, { ko: number; tries: number }>) => [...m.entries()].filter(([, v]) => v.ko >= 2 && v.ko * 2 >= v.tries).sort((a, b) => b[1].ko - a[1].ko || b[1].tries - a[1].tries)[0];
  const t = pick(byTag);
  const f = t ? null : pick(byFam);
  const insight = t ? { label: t[0], ...t[1], tag: true } : f ? { label: FAM_LABEL[f[0]].toLowerCase(), ...f[1], tag: false } : null;
  return { rows, missed, insight };
}

/** Ce qu'on garde d'une partie jouée (D-103) : sa durée et le résultat par équipe, sans aucun nom ; chacun pour soi : rien par personne. */
export function runResult(g: ParcoursState, c: ParcoursConfig, now: number): { duration_s: number; result: { bib: Bib; rank: number; cell: number; ok: number; ko: number }[] } {
  const duration_s = Math.round((g.shared.endedAt ?? gameMs(g, now)) / 1000);
  if (g.mode === 'solo') return { duration_s, result: [] };
  const r = new Map(ranking(g).map(x => [x.id, x]));
  return {
    duration_s,
    result: g.participants.map((p, i) => ({ bib: c.teams[i]!.bib, rank: r.get(p.id)!.rank, cell: g.ps[p.id]!.at, ok: g.ps[p.id]!.ok, ko: g.ps[p.id]!.ko })),
  };
}
